<template>
  <div class="lp-reviews__carousel">
    <button
      class="lp-arrow"
      type="button"
      :aria-label="props.prevLabel"
      @click="embla?.scrollPrev()"
    >
      <svg viewBox="0 0 8 14" fill="none" aria-hidden="true">
        <path d="M7 1L1 7L7 13" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" />
      </svg>
    </button>

    <div
      ref="viewportRef"
      class="lp-reviews__viewport"
      :class="{ 'is-scrolled': canScrollPrev, 'is-end': !canScrollNext }"
      role="region"
      tabindex="0"
      :aria-label="props.regionLabel"
    >
      <div class="lp-reviews__track">
        <article
          v-for="(review, index) in props.items"
          :key="index"
          class="lp-rev"
        >
          <header class="lp-rev__head">
            <span class="lp-rev__avatar" aria-hidden="true">
              {{ review.name.charAt(0) }}
            </span>
            <p class="lp-rev__name">
              {{ review.name }}
              <CountryFlag :country="review.country" :label="review.countryLabel" />
            </p>
          </header>

          <p :ref="setTextRef" class="lp-rev__text">{{ review.text }}</p>

          <!-- Only shown when the clamp actually cut the copy off. -->
          <button
            v-if="clamped[index]"
            class="lp-rev__more"
            type="button"
            @click="open(index)"
          >
            {{ $t("common.expand") }}
          </button>

          <p class="lp-rev__stars" role="img" :aria-label="props.starsLabel">
            <svg v-for="star in 5" :key="star" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M12 2.4l2.9 5.9 6.5.9-4.7 4.6 1.1 6.5-5.8-3-5.8 3 1.1-6.5L2.6 9.2l6.5-.9L12 2.4z" />
            </svg>
          </p>
        </article>
      </div>
    </div>

    <button
      class="lp-arrow"
      type="button"
      :aria-label="props.nextLabel"
      @click="embla?.scrollNext()"
    >
      <svg viewBox="0 0 8 14" fill="none" aria-hidden="true">
        <path d="M1 1L7 7L1 13" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" />
      </svg>
    </button>
  </div>

  <!-- One dialog for every card: nothing in the carousel expands in place, so
       card and section height never move. -->
  <AppModal :show="openIndex !== null" size="sm" @close="close()">
    <template v-if="openReview">
      <header class="lp-rev__head">
        <span class="lp-rev__avatar" aria-hidden="true">
          {{ openReview.name.charAt(0) }}
        </span>
        <p class="lp-rev__name">
          {{ openReview.name }}
          <CountryFlag :country="openReview.country" :label="openReview.countryLabel" />
        </p>
      </header>

      <p v-for="(paragraph, i) in openParagraphs" :key="i" class="lp-rev__modal-text">
        {{ paragraph }}
      </p>

      <p class="lp-rev__stars" role="img" :aria-label="props.starsLabel">
        <svg v-for="star in 5" :key="star" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M12 2.4l2.9 5.9 6.5.9-4.7 4.6 1.1 6.5-5.8-3-5.8 3 1.1-6.5L2.6 9.2l6.5-.9L12 2.4z" />
        </svg>
      </p>
    </template>
  </AppModal>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, nextTick } from "vue";
import EmblaCarousel, { type EmblaCarouselType } from "embla-carousel";
import AppModal from "@/components/app-modal.vue";
import CountryFlag from "./country-flag.vue";

interface Review {
  name: string;
  country: string;
  countryLabel: string;
  text: string;
}

const props = defineProps<{
  items: Review[];
  regionLabel: string;
  prevLabel: string;
  nextLabel: string;
  starsLabel: string;
}>();

const viewportRef = ref<HTMLElement>();
const embla = ref<EmblaCarouselType>();

/* Which edges get a fade. The track is masked at whichever end still has
   cards past it, so the row reads as continuing rather than being cut off. */
const canScrollPrev = ref(false);
const canScrollNext = ref(false);

const updateEdges = (): void => {
  if (!embla.value) {
    return;
  }

  canScrollPrev.value = embla.value.canScrollPrev();
  canScrollNext.value = embla.value.canScrollNext();
};

const textRefs: HTMLElement[] = [];
const setTextRef = (el: unknown): void => {
  if (el instanceof HTMLElement && !textRefs.includes(el)) {
    textRefs.push(el);
  }
};

// Whether each card's copy overflows its clamp, so "read more" only appears
// where it does something. Width and language both change the answer.
const clamped = ref<boolean[]>([]);

const measureClamp = (): void => {
  clamped.value = textRefs.map((el) => el.scrollHeight > el.clientHeight + 1);
};

const openIndex = ref<number | null>(null);
const openReview = computed(() =>
  openIndex.value === null ? null : props.items[openIndex.value],
);
const openParagraphs = computed(() =>
  (openReview.value?.text ?? "").split("\n").filter((line) => line.trim() !== ""),
);

const open = (index: number): void => {
  openIndex.value = index;
};
const close = (): void => {
  openIndex.value = null;
};

let resizeTimer: ReturnType<typeof setTimeout>;
const onResize = (): void => {
  clearTimeout(resizeTimer);
  resizeTimer = setTimeout(measureClamp, 150);
};

onMounted(async () => {
  if (viewportRef.value) {
    embla.value = EmblaCarousel(viewportRef.value, {
      align: "start",
      containScroll: "trimSnaps",
      skipSnaps: true,
      duration: 25,
    });

    embla.value
      .on("select", updateEdges)
      .on("scroll", updateEdges)
      .on("reInit", updateEdges);
    updateEdges();
  }

  await nextTick();
  measureClamp();
  window.addEventListener("resize", onResize);
});

onBeforeUnmount(() => {
  clearTimeout(resizeTimer);
  window.removeEventListener("resize", onResize);
  embla.value?.destroy();
});
</script>
